import React, { useState } from 'react';
import { useSoftKeyboardField } from '../../hooks/useSoftKeyboardField';
import { Order, Table } from '../../types';
import UserSearchIcon from '../icons/UserSearchIcon';
import UsersIcon from '../icons/UsersIcon';
import PlusIcon from '../icons/PlusIcon';
import XCircleIcon from '../icons/XCircleIcon';

interface TableCustomerSelectModalProps {
  table: Table;
  activeOrders: Order[]; // All unpaid orders for this table
  /** When > 0, show that new customers can be seated at this table */
  spareSeats?: number;
  onSelectCustomer: (customerNumber: number, customerName?: string, existingOrder?: Order) => void;
  onClose: () => void;
}

const TableCustomerSelectModal: React.FC<TableCustomerSelectModalProps> = ({
  table,
  activeOrders,
  spareSeats = 0,
  onSelectCustomer,
  onClose
}) => {
  const [newCustomerName, setNewCustomerName] = useState('');
  const [showNewCustomerInput, setShowNewCustomerInput] = useState(false);
  const nameKeyboard = useSoftKeyboardField({
    id: 'table-customer-name',
    type: 'alphanumeric',
    label: 'Customer name',
    value: newCustomerName,
    onChange: setNewCustomerName,
  });

  // Get unique customers from active orders
  const existingCustomers = activeOrders
    .filter(order => order.customerNumber !== undefined)
    .map(order => ({
      customerNumber: order.customerNumber!,
      customerName: order.customerName || `Customer ${order.customerNumber}`,
      order: order,
      totalAmount: order.totalAmount,
      itemCount: order.items.reduce((sum, item) => sum + item.quantity, 0)
    }))
    .sort((a, b) => a.customerNumber - b.customerNumber);

  // Get next customer number
  const nextCustomerNumber = existingCustomers.length > 0 
    ? Math.max(...existingCustomers.map(c => c.customerNumber)) + 1
    : 1;

  const handleSelectExisting = (customerNumber: number, order: Order) => {
    onSelectCustomer(customerNumber, order.customerName, order);
    onClose();
  };

  const handleCreateNew = () => {
    if (showNewCustomerInput) {
      // Create new customer with name
      const name = newCustomerName.trim() || `Customer ${nextCustomerNumber}`;
      onSelectCustomer(nextCustomerNumber, name);
      setNewCustomerName('');
      setShowNewCustomerInput(false);
    } else {
      // Just create new customer without name
      onSelectCustomer(nextCustomerNumber);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden my-auto">
        <div className="p-4 border-b flex justify-between items-center flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-gray-800">
              Table {table.name} - Select Customer
            </h2>
            {spareSeats > 0 && (
              <p className="text-sm text-green-700 mt-1">
                {spareSeats} seat{spareSeats !== 1 ? 's' : ''} available — you can seat a new party
              </p>
            )}
          </div>
          <button 
            onClick={onClose} 
            className="text-gray-500 hover:text-gray-800 text-2xl"
          >
            <XCircleIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="p-4 space-y-3 flex-1 min-h-0 overflow-y-auto">
          {/* Existing Customers */}
          {existingCustomers.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                Existing Customers
              </h3>
              <div className="space-y-2">
                {existingCustomers.map(({ customerNumber, customerName, order, totalAmount, itemCount }) => (
                  <button
                    key={customerNumber}
                    onClick={() => handleSelectExisting(customerNumber, order)}
                    className="w-full p-3 border-2 border-gray-200 rounded-lg hover:border-pink-400 hover:bg-pink-50 transition-colors text-left"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <UserSearchIcon className="w-5 h-5 text-gray-600" />
                        <div>
                          <div className="font-semibold text-gray-800">
                            {customerName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {itemCount} item{itemCount !== 1 ? 's' : ''} • ${totalAmount.toFixed(2)}
                          </div>
                        </div>
                      </div>
                      <div className="text-sm text-gray-500">
                        Unpaid
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* New Customer */}
          <div className="pt-2 border-t">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">
              New Customer
            </h3>
            {!showNewCustomerInput ? (
              <button
                onClick={() => setShowNewCustomerInput(true)}
                className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-pink-400 hover:bg-pink-50 transition-colors flex items-center justify-center gap-2"
              >
                <PlusIcon className="w-5 h-5 text-gray-600" />
                <UsersIcon className="w-5 h-5 text-gray-600" />
                <span className="font-semibold text-gray-700">
                  Add New Customer
                </span>
              </button>
            ) : (
              <div className="space-y-2">
                <input
                  type="text"
                  value={newCustomerName}
                  placeholder={`Customer ${nextCustomerNumber} (optional name)`}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-400"
                  data-soft-keyboard-handled
                  {...nameKeyboard}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleCreateNew();
                    } else if (e.key === 'Escape') {
                      setShowNewCustomerInput(false);
                      setNewCustomerName('');
                    }
                  }}
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleCreateNew}
                    className="flex-1 px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 font-semibold"
                  >
                    Create Customer
                  </button>
                  <button
                    onClick={() => {
                      setShowNewCustomerInput(false);
                      setNewCustomerName('');
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableCustomerSelectModal;

